import { DotsVerticalIcon } from '@heroicons/react/outline';
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { createAppBasePath } from '../../consts/route-consts';
import { exportApp, importApp } from '../../services/app/app-service';
import { APP_MODES } from '../../consts/common-consts';
const dayjs = require('dayjs');
const relativeTime = require('dayjs/plugin/relativeTime');
const isToday = require('dayjs/plugin/isToday');
const isYesterday = require('dayjs/plugin/isYesterday');
dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

const formatDate = (lastEdited) => {
  const date = dayjs(lastEdited);
  if (date.isToday()) {
    return `Today at ${date.format('h:mm A')}`;
  } else if (date.isYesterday()) {
    return `Yesterday at ${date.format('h:mm A')}`;
  } else {
    return `on ${date.format('MMMM D, YYYY')}`;
  }
};

export default function Tile({ app, onDelete, onExport, onImport, onImportRequest }) {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const fileInputRef = useRef(null);

  const toggleMenu = (event) => {
    event.stopPropagation(); // Prevents the click from bubbling up to the tile click
    setShowMenu(!showMenu);
  };

  const handleDelete = (appId) => {
    onDelete(appId)
    setShowMenu(false);
  };

  const handleExport = async (event) => {
    event.stopPropagation();
    setShowMenu(false);
    try {
      const { response, status } = await exportApp(app.appId);
      if (status === 200) {
        // Create a blob and download the file
        const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${app.name}-config-export.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        if (onExport) onExport(app.appId, true);
      } else {
        alert('Export failed. Please try again.');
        if (onExport) onExport(app.appId, false);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
      if (onExport) onExport(app.appId, false);
    }
  };

  const handleImportClick = (event) => {
    event.stopPropagation();
    setShowMenu(false);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const fileContent = await file.text();
      const configData = JSON.parse(fileContent);

      // Trigger confirmation dialog with file data
      if (onImportRequest) {
        onImportRequest(app.appId, app.name, configData);
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('Invalid JSON file. Please check the file format.');
    }

    // Reset the input
    event.target.value = '';
  };

  const navigateToApp = () => {
    const appPath = `${createAppBasePath(app.appId)}${app.appMode === APP_MODES.PROMPT_BASED ? '/agent' : '/journey'}`;
    navigate(appPath);
  };

  return (
    <div className="bg-white group rounded-lg border cursor-pointer border-gray-300 shadow-sm hover:shadow-md transition-shadow" onClick={navigateToApp}>
      <div className="flex relative py-4 pl-4 pr-2 gap-4">
        <div className="flex justify-center shrink-0 items-center h-20 w-20 rounded-md bg-gray-700 text-white text-3xl font-bold uppercase">
          {app.name?.charAt(0)}
        </div>
        <div className="flex-grow max-w-[180px]:">
          <div className="font-medium text-lg text-gray-900 dark:text-white mb-1 break-words">{app.name}</div>
          <p className="text-gray-600 dark:text-white text-xs">
            Last Edited {formatDate(app.modifiedAt)}
          </p>
        </div>
        <div className="relative" onClick={e => e.stopPropagation()}>
          <DotsVerticalIcon className="w-6 h-6 text-gray-600" onClick={toggleMenu} />
          {showMenu && (
            <div className="absolute right-0 mt-2 bg-white border rounded shadow-lg py-1 z-10 min-w-[160px]">
              <div
                className="px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer text-sm"
                onClick={handleExport}
              >
                Export Config
              </div>
              <div
                className="px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer text-sm"
                onClick={handleImportClick}
              >
                Import Config
              </div>
              <div className="border-t border-gray-200 my-1"></div>
              <div
                className="px-4 py-2 hover:bg-red-100 hover:text-red-700 cursor-pointer text-sm"
                onClick={() => handleDelete(app.appId)}
              >
                Delete Assistant
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>
      </div>
    </div>
  );
}